/**
 * Web Push subscription helper.
 *
 * Flow:
 *   1. Fetch the VAPID public key from the backend.
 *   2. Register a service worker if not already.
 *   3. Subscribe via PushManager and send the subscription to the backend.
 *
 * Browsers block calls to `Notification.requestPermission()` outside a user
 * gesture, so this must be invoked from a click/keydown handler.
 *
 * Returns one of:
 *   - 'ok'          — subscribed successfully
 *   - 'denied'      — user denied permission
 *   - 'unsupported' — browser lacks SW / PushManager (e.g. incognito Chrome)
 *   - 'error'       — unexpected failure (e.g. FCM blocked)
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlB64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function ensurePushSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';

  // Permission must be requested under a user gesture.
  let perm = Notification.permission;
  if (perm !== 'granted') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') return 'denied';

  try {
    // Register or reuse the service worker (unified SW for PWA + push).
    const reg = await navigator.serviceWorker.register('/service-worker.js');
    await navigator.serviceWorker.ready;

    // Check if already subscribed.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await axios.get(`${API}/push/public-key`);
      if (!data?.public_key) return 'error';
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      });
    }

    // Send subscription to backend (idempotent).
    const json = sub.toJSON();
    await axios.post(`${API}/push/subscribe`, {
      endpoint: json.endpoint,
      keys: json.keys,
    });
    return 'ok';
  } catch (e) {
    return 'error';
  }
}

export function isPushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
}
